// src/pages/ArticlePage.js
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // <<< NOUVEL IMPORT
import ReactMarkdown from 'react-markdown';
import { blogPostsData } from '../data/blogPostsData';
import { slugify } from '../utils/helpers'; 
import SocialShareButtons from '../components/common/SocialShareButtons';
import RelatedPostsSection from '../components/blog/RelatedPostsSection';
import pb from '../lib/pocketbase';
import './ArticlePage.css';

function ArticlePage() {
  const { articleSlug } = useParams();
  const [pbPosts, setPbPosts] = React.useState(null);
  const [pbLoaded, setPbLoaded] = React.useState(false);

  const siteUrl = "https://infocolab.com";

  const fileUrl = (record, fileField, thumb) => {
    try {
      if (!record || !record[fileField]) return '';
      return pb.files.getUrl(record, record[fileField], thumb ? { thumb } : undefined);
    } catch {
      return '';
    }
  };

  React.useEffect(() => {
    let canceled = false;

    const load = async () => {
      try {
        const res = await pb.collection('site_blog_posts').getList(1, 500, { sort: 'sort_order, -published_at, -created', filter: 'is_published = true' });
        const items = (res?.items ?? []).map((it) => ({
          slug: it.slug,
          title: it.title,
          date: it.published_at || it.created,
          author: it.author || "",
          tags: it.tags || [],
          imageUrl: fileUrl(it, 'cover_image', '1400x0') || '',
          excerpt: it.excerpt || '',
          content: it.content_md || '',
        })).filter((it) => it.slug && it.title);
        if (!canceled) setPbPosts(items);
      } catch {
        if (!canceled) setPbPosts(null);
      } finally {
        if (!canceled) setPbLoaded(true);
      }
    };

    load();
    return () => { canceled = true; };
  }, []);

  const sourcePosts = Array.isArray(pbPosts) && pbPosts.length ? pbPosts : blogPostsData;
  const normalizedSlug = (() => {
    try {
      return decodeURIComponent(articleSlug || '');
    } catch {
      return articleSlug || '';
    }
  })();
  const article = sourcePosts.find(post => post.slug === normalizedSlug);

  const shouldWaitForPb = !pbLoaded && pbPosts === null;

  if (shouldWaitForPb) {
    return (
      <div className="container article-page-container">
        <p>Chargement de l'article…</p>
      </div>
    );
  }

  if (!article) {
    return (
      <>
        <Helmet>
            <title>Article non trouvé - INFO COLAB</title>
            <meta name="description" content="Cet article du blog INFO COLAB n'a pas été trouvé ou a été déplacé." />
        </Helmet>
        <Navigate to="/404" replace />
      </>
    );
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : `${siteUrl}/blog/${article.slug}`;
  const metaDescription = article.excerpt 
    ? (article.excerpt.length > 155 ? article.excerpt.substring(0, 152) + '...' : article.excerpt) 
    : `Lisez l'article "${article.title}" sur le blog de INFO COLAB.`;
  const ogImageUrl = article.imageUrl
    ? (String(article.imageUrl).startsWith('http') ? article.imageUrl : `${siteUrl}${article.imageUrl}`)
    : `${siteUrl}/images/og-image-blog-default.jpg`; // Crée une image par défaut

  return (
    <> {/* Fragment pour Helmet et le contenu */}
      <Helmet>
        <title>{`${article.title} - Blog INFO COLAB`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={currentUrl} />
        {/* Open Graph Tags */}
        <meta property="og:title" content={`${article.title} - Blog INFO COLAB`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="INFO COLAB" />
        {article.author && <meta property="article:author" content={article.author} />}
        {article.date && <meta property="article:published_time" content={article.date} />}
        {article.tags && article.tags.map(tag => (
          <meta property="article:tag" content={tag} key={`og-tag-${tag}`} />
        ))}
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${article.title} - Blog INFO COLAB`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <div className="container article-page-container">
        <Link to="/blog" className="back-to-blog-link" aria-label="Retour à la liste des articles du blog">
          &larr; Retour au Blog
        </Link>

        <header className="article-header">
          <h1>{article.title}</h1>
          <p className="article-meta">
            Publié le {new Date(article.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            {article.author && ` par ${article.author}`}
          </p>
          {article.tags && article.tags.length > 0 && (
            <div className="article-tags-display">
              {article.tags.map(tag => (
                <Link key={tag} to={`/blog/tag/${slugify(tag)}`} className="tag-item-article">
                  {tag}
                </Link>
              ))}
            </div>
          )}
        </header>

        {article.imageUrl && (
          <img src={article.imageUrl} alt={`Illustration principale pour l'article ${article.title}`} className="article-main-image" />
        )}

        <div className="article-content">
          <ReactMarkdown>{article.content}</ReactMarkdown>
        </div>

        <SocialShareButtons 
          url={currentUrl} 
          title={article.title} 
          excerpt={article.excerpt} 
        />
        
        <RelatedPostsSection 
          currentArticleSlug={article.slug}
          currentArticleTags={article.tags || []}
          maxPosts={3}
          posts={sourcePosts}
        />
      </div>
    </>
  );
}

export default ArticlePage;